import React, { useState } from 'react';
import { Exercise, WorkoutLogSet } from '../../types/database.types';
import { Check, Info, ChevronDown, ChevronUp, Plus, Minus, Dumbbell, Flame, TrendingUp } from 'lucide-react';

interface ExerciseCardProps {
  exercise: Exercise;
  exerciseIndex: number;
  sets: WorkoutLogSet[];
  onCompleteSet: (setNumber: number, weightKg: number, reps: number, restSeconds: number) => void;
  onAddSet: () => void;
}

export const ExerciseCard: React.FC<ExerciseCardProps> = ({
  exercise,
  exerciseIndex,
  sets,
  onCompleteSet,
  onAddSet,
}) => {
  const [showDemo, setShowDemo] = useState(false);

  // Cargas e Reps editáveis da série atual
  const [activeLoads, setActiveLoads] = useState<{ [key: number]: number }>({
    1: 28,
    2: 28,
    3: 30,
    4: 30,
  });

  const [activeReps, setActiveReps] = useState<{ [key: number]: number }>({
    1: 10,
    2: 10,
    3: 8,
    4: 8,
  });

  const handleWeightDelta = (setNumber: number, delta: number) => {
    setActiveLoads((prev) => ({
      ...prev,
      [setNumber]: Math.max(0, (prev[setNumber] ?? 24) + delta),
    }));
  };

  const handleRepsDelta = (setNumber: number, delta: number) => {
    setActiveReps((prev) => ({
      ...prev,
      [setNumber]: Math.max(1, (prev[setNumber] ?? 10) + delta),
    }));
  };

  const completedSets = sets.filter((s) => s.completed).length;
  const isAllDone = sets.length > 0 && completedSets === sets.length;

  return (
    <div
      className={`rounded-[26px] p-5 border transition-all duration-300 relative overflow-hidden ${
        isAllDone
          ? 'border-[#30D158]/50 bg-gradient-to-b from-[#30D158]/10 via-[#1C1C1E] to-[#1C1C1E] shadow-[0_4px_24px_rgba(48,209,88,0.15)]'
          : 'bg-white dark:bg-[#1C1C1E] border-slate-200 dark:border-white/[0.08] hover:border-slate-300 dark:hover:border-white/[0.14]'
      }`}
    >
      {/* 1. Header do Exercício */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center tabular-nums font-extrabold text-xs border shrink-0 ${
              isAllDone
                ? 'bg-[#30D158] text-black border-[#30D158] shadow-[0_2px_10px_rgba(48,209,88,0.4)]'
                : 'bg-slate-100 dark:bg-[#2C2C2E] text-slate-700 dark:text-slate-300 border-slate-200 dark:border-white/[0.08]'
            }`}
          >
            {exerciseIndex + 1}
          </div>

          <div>
            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">{exercise.name}</h3>
            
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#30D158]/15 text-[#30D158] border border-[#30D158]/30">
                {exercise.target_muscle}
              </span>
              {exercise.secondary_muscles.map((sec) => (
                <span
                  key={sec}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-[#2C2C2E] text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/[0.04]"
                >
                  {sec}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Botão de Demonstração / GIF */}
        <button
          onClick={() => setShowDemo(!showDemo)}
          className="px-3 py-1.5 rounded-full bg-slate-100 dark:bg-[#2C2C2E] hover:bg-slate-200 dark:hover:bg-[#3A3A3C] text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/[0.08] transition flex items-center gap-1 text-[11px] font-semibold apple-press"
        >
          <Info className="w-3.5 h-3.5 text-[#0A84FF]" />
          <span>{showDemo ? 'Ocultar' : 'Execução'}</span>
          {showDemo ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* 2. Banner de Instrução Biomecânica (Expansível) */}
      {showDemo && (
        <div className="mt-4 p-4 rounded-[20px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.08] space-y-3 animate-fadeIn">
          {exercise.demo_url && (
            <div className="rounded-[16px] overflow-hidden border border-slate-200 dark:border-white/[0.08] max-h-52 bg-black flex items-center justify-center">
              <img
                src={exercise.demo_url}
                alt={exercise.name}
                className="w-full h-48 object-cover opacity-90"
                loading="lazy"
              />
            </div>
          )}

          <div className="text-xs text-slate-700 dark:text-slate-300 space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#0A84FF] flex items-center gap-1">
              <Flame className="w-3.5 h-3.5 text-[#FF9F0A]" /> Instrução Técnica & Biomecânica
            </span>
            <p className="leading-relaxed text-slate-600 dark:text-slate-300">{exercise.instructions}</p>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-white/[0.06]">
            <span>Descanso Ativo Recomendado:</span>
            <span className="text-[#30D158] font-bold tabular-nums">{exercise.default_rest_seconds}s</span>
          </div>
        </div>
      )}

      {/* 3. Tabela de Séries Profissional */}
      <div className="mt-4 space-y-2">
        <div className="grid grid-cols-12 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 px-3">
          <div className="col-span-2">SÉRIE</div>
          <div className="col-span-3">ANTERIOR</div>
          <div className="col-span-4 text-center">CARGA (KG)</div>
          <div className="col-span-3 text-right">REPS & CHECK</div>
        </div>

        {sets.map((set) => {
          const currentLoad = activeLoads[set.set_number] ?? (set.weight_kg || 28);
          const currentReps = activeReps[set.set_number] ?? (set.reps_performed || 10);
          const isDone = set.completed;

          return (
            <div
              key={set.set_number}
              className={`grid grid-cols-12 items-center px-3 py-2.5 rounded-[18px] border transition-all duration-200 ${
                isDone
                  ? 'bg-[#30D158]/10 border-[#30D158]/40 text-[#30D158] shadow-sm'
                  : 'bg-slate-50 dark:bg-[#2C2C2E]/70 border-slate-200 dark:border-white/[0.06] text-slate-800 dark:text-slate-200 hover:border-slate-300 dark:hover:border-white/[0.1]'
              }`}
            >
              {/* Série # */}
              <div className="col-span-2 flex items-center gap-1 tabular-nums font-bold text-xs text-slate-900 dark:text-white">
                #{set.set_number}
              </div>

              {/* Carga Anterior de Referência */}
              <div className="col-span-3 tabular-nums text-[11px] text-slate-400">
                {set.previous_weight_kg ? (
                  <span className="flex items-center gap-1">
                    <span className="font-bold text-slate-700 dark:text-slate-300">{set.previous_weight_kg}k</span>
                    <span className="text-slate-400 dark:text-slate-600">x{set.previous_reps}</span>
                  </span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-600">-</span>
                )}
              </div>

              {/* Ajustador Dinâmico de Carga */}
              <div className="col-span-4 flex items-center justify-center gap-1">
                {!isDone ? (
                  <>
                    <button
                      onClick={() => handleWeightDelta(set.set_number, -2.5)}
                      className="w-7 h-7 rounded-full bg-slate-200 dark:bg-[#3A3A3C] hover:bg-slate-300 dark:hover:bg-[#48484A] text-slate-800 dark:text-slate-200 flex items-center justify-center font-bold text-xs active:scale-90 transition apple-press"
                    >
                      -
                    </button>

                    <div className="w-12 text-center tabular-nums font-bold text-sm text-slate-900 dark:text-white">
                      {currentLoad}
                      <span className="text-[9px] text-slate-400 ml-0.5">kg</span>
                    </div>

                    <button
                      onClick={() => handleWeightDelta(set.set_number, 2.5)}
                      className="w-7 h-7 rounded-full bg-slate-200 dark:bg-[#3A3A3C] hover:bg-slate-300 dark:hover:bg-[#48484A] text-slate-800 dark:text-slate-200 flex items-center justify-center font-bold text-xs active:scale-90 transition apple-press"
                    >
                      +
                    </button>
                  </>
                ) : (
                  <span className="tabular-nums font-bold text-sm text-[#30D158]">
                    {set.weight_kg} kg
                  </span>
                )}
              </div>

              {/* Reps e Conclusão com 1 toque */}
              <div className="col-span-3 flex items-center justify-end gap-2">
                {!isDone ? (
                  <>
                    <button
                      onClick={() => handleRepsDelta(set.set_number, 1)}
                      className="px-2 py-1 rounded-[10px] bg-slate-200 dark:bg-[#3A3A3C] tabular-nums font-bold text-xs text-slate-800 dark:text-slate-200 border border-slate-300 dark:border-white/[0.06] apple-press"
                      title="Alterar repetições"
                    >
                      {currentReps}x
                    </button>

                    <button
                      onClick={() =>
                        onCompleteSet(
                          set.set_number,
                          currentLoad,
                          currentReps,
                          exercise.default_rest_seconds
                        )
                      }
                      className="w-8 h-8 rounded-full bg-[#30D158] hover:bg-[#28C846] text-black flex items-center justify-center shadow-[0_2px_8px_rgba(48,209,88,0.35)] active:scale-90 transition-all cursor-pointer apple-press"
                      title="Concluir série e disparar descanso"
                    >
                      <Check className="w-4 h-4 stroke-[3]" />
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-1.5 tabular-nums text-xs font-bold text-[#30D158]">
                    <span>{set.reps_performed} reps</span>
                    <div className="w-6 h-6 rounded-full bg-[#30D158] text-black flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 stroke-[3]" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 4. Rodapé do Card com Progresso e Botão Adicionar Série */}
      <div className="mt-3 pt-2.5 border-t border-slate-200 dark:border-white/[0.06] flex items-center justify-between">
        <button
          onClick={onAddSet}
          className="text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-[#30D158] transition flex items-center gap-1.5 apple-press"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Adicionar Série #{sets.length + 1}</span>
        </button>

        <span className="text-[11px] tabular-nums text-slate-500">
          Progresso: <strong className={isAllDone ? 'text-[#30D158]' : 'text-slate-700 dark:text-slate-300'}>{completedSets}/{sets.length} séries</strong>
        </span>
      </div>
    </div>
  );
};
